import './DynamicProfile.scss';
import { Extent, Map, MapView, SpatialReference } from 'cd-eft/utils/esriTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import stores from 'cd-eft/models/Stores';
import { Row, Col } from 'reactstrap';
import { mdiCrosshairsGps, mdiFormatListBulleted } from '@mdi/js';
import Icon from '@mdi/react';
import { Loader } from '../LoadingSpinner/LoadingSpinner';

const libraryStore = stores.AppStore.projectsLibraryStore;
const terraceDesignStore = stores.AppStore.terraceDesignStore;

interface IDynamicTerraceProfileProps {
  profileStore: DynamicProfileStoreBase | undefined;
  editable: boolean;
  generateInitialProfiles;
  uiReady: boolean;
}

const DynamicTerraceProfile = (props: IDynamicTerraceProfileProps) => {
  const [waitingInit, setWaitingInit] = useState(true);
  const [legendVisible, setLegendVisible] = useState(true);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const profileMapViewRef = useRef<MapView>();

  const startCoordinateReadout = useCallback(() => {
    if (profileMapViewRef.current) {
      props.profileStore?.startCoordinateReadout();
    }
  }, [props.profileStore]);

  const stopCoordinateReadout = useCallback(() => {
    if (props.profileStore) {
      props.profileStore.stopCoordinateReadout();
    }
  }, []);

  const toggleCoordinates = () => {
    setShowCoordinates(prevState => !prevState);
  };

  useEffect(() => {
    if (showCoordinates) {
      startCoordinateReadout();
    } else {
      stopCoordinateReadout();
    }
  }, [showCoordinates]);

  if (!profileMapViewRef.current) {
    const spatialReference = new SpatialReference({ wkid: 102965 });
    const extent = new Extent({ xmin: 0, ymin: 0, xmax: 100, ymax: 210, spatialReference });

    profileMapViewRef.current = new MapView({ map: new Map({ layers: [] }), extent, spatialReference });
  }

  useEffect(() => {
    if (profileMapViewRef.current && props.uiReady) {

      const sr: SpatialReference = libraryStore.projectSpatialReference!;
      props.profileStore?.initialize(profileMapViewRef.current, sr, props.editable, 'terrace');
      setWaitingInit(false);

      profileMapViewRef.current.container = document.getElementById('terrace-Profile') as HTMLDivElement;
      if (props.profileStore) {props.profileStore.startLengthyProcess = (state: boolean) => setWaitingInit(state)};
      // Checks that we have a terrace id in memory before making API calls
      if (terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId) {props.generateInitialProfiles()};
    }
  }, [props.editable, props.profileStore, props.uiReady]);


  return (
    <div id={'profile-and-peripherals-container'}>
      <div id={'terrace-Profile'} style={{ position: "relative" }}>
        <div id="terrace-profile-coordinates-container"></div>
        <div className={`profile-legend-container ${legendVisible ? 'visible' : 'hidden'}`} role="region" aria-label="Terrace Legend">
          <Row>
            <Col className="col-15">Constructed Terrace</Col>
            <Col><div className="solidline" /></Col>
          </Row>
          <Row>
            <Col className="col-7">Ground Surface</Col>
            <Col><div className="dashedline-green" /></Col>
          </Row>
          <Row>
            <Col className="col-7">Channel</Col>
            <Col><div className="solidline-dark-blue" /></Col>
          </Row>
          <Row>
            <Col className="col-7">Settled Terrace</Col>
            <Col><div className="dashedline-black" /></Col>
          </Row>
          <Row>
            <Col className="col-7">Water Level</Col>
            <Col><div className="solidline-light-blue" /></Col>
          </Row>
          {/* <Row>
            <Col className="col-7">Drain Location</Col>
            <img src={drainIcon} height={'16px'} />
          </Row>
          <Row>
            <Col className="col-7">Block Location</Col>
            <img src={blockIcon} height={'16px'} />
          </Row> */}
        </div>
        <div className="mt-5" hidden={!waitingInit}>
          <Loader message={'Loading profiles ...'} />
        </div>
      </div>
      <div className="profile-esri-btns">
          <div title='Legend' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => setLegendVisible(!legendVisible)}><Icon path={mdiFormatListBulleted} size={0.9} horizontal/></div>
          <div title='Show Coordinates' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={toggleCoordinates}><Icon path={mdiCrosshairsGps} size={0.8} /></div>
          <div title='Zoom In' className='esri-icon-zoom-in-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) {profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(0.75) }}}></div>
          <div title='Zoom Out' className='esri-icon-zoom-out-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) {profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(1.25) }}}></div>
          <div title='Zoom to Extent' className='esri-icon-applications esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current && props.profileStore?.maxExtent) {profileMapViewRef.current.extent = props.profileStore?.maxExtent }}}></div>
      </div>
    </div>
  );
};

export { DynamicTerraceProfile };
